<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from "vue";
import { useModals } from "@/composables/useModals";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { policyUrl, rulesUrl, agreementUrl, advertisingUrl } from "@/utils/provider";
import { useRoute, RouterLink } from "vue-router";
import { sendMetrik, trackPixel } from "@/utils/metrika";
import { useGameHistory } from "@/composables/useGameHistory";
import { formatText } from "@/utils/formatter";
import { declension } from "@/utils/functions";
import BaseInput from "@/components/ui/BaseInput.vue";
import BasePhoneInput from "@/components/ui/BasePhoneInput.vue";
import BaseCheckbox from "@/components/ui/BaseCheckbox.vue";
import BaseSelect from "@/components/ui/BaseSelect.vue";
import BaseTextarea from "@/components/ui/BaseTextarea.vue";
import BaseFile from "@/components/ui/BaseFile.vue";
import BasePassword from "@/components/ui/BasePassword.vue";
import BasePasswordRepeat from "@/components/ui/BasePasswordRepeat.vue";
import BaseNumberInput from "@/components/ui/BaseNumberInput.vue";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";
import BaseCopyTooltip from "@/components/ui/BaseCopyTooltip.vue";

import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import { Pagination} from "swiper/modules";
import SliderPagination from "@/components/blocks/slider/SliderPagination.vue";

const storeField = useFieldsStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const { remainingTime } = useGameHistory();
const route = useRoute();

const selectOptions = ref([]);
const pointerEventsNone = ref(false);
const email = ref("");
const name = ref("");
const modal = ref(null);
 const galleryRef = ref(null);

const openGallery = () => {
	const firstImage = document.querySelector('.modal-prizes__swiper .spotlight');
	if (firstImage) {
		firstImage.click();
	}
};

const password = defineModel();

const { activeModal, modalId, modalContent, sizeWidthModal, closeModal, openModal } = useModals();

const addressModalLocked = ref(false);
const addressModalShown = ref(false);

watch(
	() => storeRequests.userData,
	(val) => {
		if (
			!addressModalShown.value &&
			storeRequests.isAuthenticated &&
			val?.needs_to_fill_address === true &&
			!storeSettings.statusStockPast
		) {
			addressModalShown.value = true;
			setTimeout(() => {
				addressModalLocked.value = true;
				openModal('isNotAddressPhysical');
			}, 2000);
		}
	},
	{ deep: true },
);

watch(modalId, (val) => {
	if (val === 'sentChangeUserProfile') {
		addressModalLocked.value = false;
	}
});

const userReceiptsTitle = {
	upload_available: "Загрузка чека доступна",
	upload_unavailable: "Загрузка чека недоступна, акция неактивна",
	maximum_receipt: "Ты уже загрузили максимальное количество чеков за период акции — 20 штук.",
	maximum_receipt_in_current_period: "Ты достиг максимального количества загрузок чеков за один день — 3 штуки. Новые загрузки будут доступны завтра.",
	maximum_upload_receipt: "У тебя есть чеки, ожидающие проверки. Чтобы не превысить лимит, новые чеки можно будет загрузить после завершения модерации.",
	maximum_upload_receipt_in_current_period: "У тебя есть чеки, ожидающие проверки. Чтобы не превысить лимит, новые чеки можно будет загрузить после завершения модерации.",
};

const userReceiptsStatus = (status) => {
	return userReceiptsTitle[status];
};

watch(
	() => storeRequests.isAuthenticated,
	(newVal) => {
		if (newVal) {
			storeField.updateFields("firstName", "disabledBtn", true);
			storeField.updateFields("email", "disabledBtn", true);
			storeField.updateFields("lastName", "disabledBtn", true);
			storeField.updateFields("phone", "disabledBtn", true);
		}
	},
);

watch(
	() => storeRequests.userData,
	(val) => {
		if (val?.email) email.value = val.email;
		if (val?.first_name) name.value = val.first_name;
	},
	{ immediate: true },
);

const timeAr = computed(() => remainingTime.value?.split(':'));

const copy = async (text, show) => {
	await navigator.clipboard.writeText(text);
	show();
};

const download = async (id, show) => {
	const data = await storeRequests.downloadCertificate(id);

	if (!data) return;
	const url = window.URL.createObjectURL(data);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", "certificate.pdf");
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
	link.remove();

	show();
};

onMounted(async () => {
	selectOptions.value = await storeRequests.topics();
	const script = document.createElement("script");
	const scriptPath = "/libs/spotlight.bundle.js";
	script.src = scriptPath;
	modal.value.append(script);
});

onUnmounted(() => {
	pointerEventsNone.value = false;
});
</script>
<template>
	<div style="position: absolute; bottom: 100%; overflow: hidden">
		<div id="apm-widget"></div>
	</div>
	<div :class="['modal', { 'is-open': activeModal }]" @click.self="addressModalLocked ? null : closeModal" ref="modal">
		<Transition>
			<div v-if="activeModal" :class="['modal__container', sizeWidthModal]">
				<button v-if="!(activeModal && modalId === 'isNotAddressPhysical' && addressModalLocked)" class="modal-close" @click="closeModal">
					<svg width="40" height="40"><use href="#x"></use></svg>
				</button>
				<!-- Обратная связь -->
				<div class="modal-content" v-if="modalId === 'feedback'">
					<h5 class="modal-title">Обратная связь</h5>
					<form class="form" @submit.prevent="storeRequests.feedback()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" v-model="name"> Имя </BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" placeholder="Email" :required="true" state-key="email" v-model="email">Email</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseSelect :select-options></BaseSelect>
							</div>
							<div class="form__wrap">
								<BaseTextarea :max-length="600" state-key="message" placeholder="Сообщение">Сообщение</BaseTextarea>

								<BaseFile />
							</div>
						</div>
						<div class="form__footer">
							<div class="form__checkboxes">
								<BaseCheckbox :required="true" state-key="policy">Согласен(на) с <a :href="agreementUrl" target="_blank" rel="noopener noreferrer">обработкой персональных данных</a></BaseCheckbox>
								<BaseCheckbox :required="false" state-key="agreement">Даю согласие на получение <a :href="advertisingUrl" target="_blank" rel="noopener noreferrer">информационной и рекламной рассылки</a></BaseCheckbox>
							</div>
							<button :disabled="storeField.isBtnDisabled(['email', 'firstName', 'select', 'message', 'policy'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Авторизация -->
				<div class="modal-content" v-if="modalId === 'authorization'">
					<h5 class="modal-title">Авторизация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.authorization()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" placeholder="Email" :required="true" state-key="email"> Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password"> Пароль </BasePassword>
								<div class="form__text-btn-inner">
									<svg width="20" height="20"><use href="#icons_forgot_pass"></use></svg>
									<button @click="openModal('recoveryPassword')" class="form__text-btn" type="button">Забыли пароль?</button>
								</div>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['email', 'password'])" :class="['send-button form__btn btn', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Войти</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span> Нет аккаунта? <button @click="(openModal('registration'))" class="">Регистрация</button></span>
							</div>
						</div>
					</form>
				</div>

				<!-- Требуется авторизация -->
				<div class="modal-content" v-if="modalId === 'authorizationRequired'">
					<h5 class="modal-title">Требуется авторизация</h5>
					<form class="form form-grid">
						<div class="form__footer">
							<div class="form__btn-wrapper">
								<span>У меня уже есть аккаунт</span>
								<button @click="openModal('authorization')" class="send-button btn form__btn --blue" type="button">Войти</button>
							</div>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span>У меня нет аккаунта</span>
								<button @click="(openModal('registration'))" class="send-button btn form__btn --blue" type="button">Регистрация</button>
							</div>
						</div>
					</form>
				</div>

				<!-- Регистрация -->
				<div class="modal-content" v-if="modalId === 'registration'">
					<h5 class="modal-title">Регистрация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.registration()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName"> Имя</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Фамилия" :required="true" state-key="lastName">Фамилия</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="Номер телефона" state-key="phone">Номер телефона </BasePhoneInput>
							</div>
						</div>
						<div class="form__checkboxes">
							<BaseCheckbox :required="true" state-key="rules">Я согласен(на) с <a :href="rulesUrl" target="_blank" rel="noopener noreferrer">правилами акции</a> </BaseCheckbox>
							<!-- <BaseCheckbox :required="true" state-key="agreement">Я согласен(на) на <a :href="agreementUrl" target="_blank" rel="noopener noreferrer">обработку моих персональных данных</a> </BaseCheckbox> -->
							<BaseCheckbox :required="false" state-key="agreeToAdvertising">Я согласен(на) на получение <a :href="advertisingUrl" target="_blank" rel="noopener noreferrer">информационной и рекламной рассылки</a></BaseCheckbox>
						</div>
						<div class="form__footer">
							<button @click="trackPixel('VIEW', 'registration'), smartPixel('registration_success')" :disabled="storeField.isBtnDisabled(['firstName', 'lastName', 'phone', 'email', 'rules'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Регистрация</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper">
								<span> Уже есть аккаунт? <button @click="openModal('authorization')" class="">Войти</button></span>
							</div>
						</div>
					</form>
				</div>

				<!-- Восстановление пароля -->
				<div class="modal-content" v-if="modalId === 'recoveryPassword'">
					<h5 class="modal-title">Восстановление пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.recoveryPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['email'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Изменение пароля -->
				<div class="modal-content" v-if="modalId === 'changeUserPassword'">
					<h5 class="modal-title">Изменение пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password" v-model="password"> Новый пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)"> Повторите новый пароль </BasePasswordRepeat>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['password', 'passwordRepeat'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Сохранить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Изменение данных -->
				<div class="modal-content" v-if="modalId === 'changeUserProfile'">
					<h5 class="modal-title">Добавление адреса</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserProfile()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="ФИО" :required="true" state-key="fio" v-model="storeField.fields.fio.title"> ФИО</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email" v-model="storeRequests.userData.email">Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="Номер телефона" state-key="phone">Номер телефона </BasePhoneInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" :required="true" placeholder="Город" state-key="city">Город</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" :required="true" placeholder="Улица" state-key="street">Улица</BaseInput>
							</div>
							<div class="form__row-inputs">
								<div class="form__wrap">
									<BaseInput type="text" :required="true" placeholder="Дом" state-key="house">Дом</BaseInput>
								</div>
								<div class="form__wrap">
									<BaseInput type="text" :required="true" placeholder="Квартира" state-key="apartment">Квартира</BaseInput>
								</div>
								<div class="form__wrap">
									<BaseNumberInput
										:required="true"
										placeholder="000000"
										mask="999999"
										maskPlaceholder=""
										state-key="zipCode"
									>
										Индекс
									</BaseNumberInput>
								</div>
							</div>
						</div>
						<div class="form__footer --small-gap">
							<button :disabled="storeField.isBtnDisabled(['fio', 'email', 'phone', 'city', 'street', 'house', 'apartment', 'zipCode'])" :class="['send-button form__btn btn --blue', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Регистрация завершена -->
				<div class="modal-content" v-if="modalId === 'registrationEnd'">
					<h5 class="modal-title">Регистрация завершена</h5>
					<p class="modal-subtitle">Пароль отправлен на указанный e-mail.</p>
					<p class="modal-subtitle">Если Вы не получили письмо в течение нескольких минут, пожалуйста, проверьте папку «Спам».</p>
					<div class="form form-grid">
						<button @click="openModal('authorization')" class="send-button btn form__btn --blue" type="button">Войти</button>
					</div>
				</div>

				<!-- Пройти верефикацию -->
				<div class="modal-content" v-if="modalId === 'notVerified'">
					<h5 class="modal-title">Ваш аккаунт ещё не активирован</h5>
					<p class="modal-subtitle">Пожалуйста, перейдите по ссылке из письма, отправленного на Ваш e-mail, чтобы завершить регистрацию.</p>
					<p class="modal-subtitle">Не нашли письмо? Проверьте папку «Спам» или запросите повторное письмо</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --blue" type="button" @click="storeRequests.repeatVerification()">Запросить повторно</button>
					</div>
				</div>

				<!-- Пройти верефикацию -->
				<div class="modal-content" v-if="modalId === 'errorVerified'">
					<h5 class="modal-title">Ошибка верификации</h5>
					<p class="modal-subtitle">Срок действия Вашей ссылки для подтверждения истёк или ссылка недействительна. Пожалуйста, запросите новую ссылку.</p>
					<div class="form form-grid">
						<div class="form__wrap">
							<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
						</div>
						<button :disabled="storeField.isBtnDisabled(['email'])" class="send-button btn form__btn --blue" type="button" @click="storeRequests.repeatVerification()">Запросить повторно</button>
					</div>
				</div>

				<!-- Пройти верефикацию повторное письмо -->
				<div class="modal-content" v-if="modalId === 'endRepeatVerified'">
					<h5 class="modal-title">На Ваш e-mail отправлено письмо с данными для входа</h5>
					<p class="modal-subtitle">Пожалуйста, перейдите по ссылке в письме, чтобы активировать Ваш аккаунт и ознакомиться с паролем. Не нашли письмо? Проверьте папку «Спам» или запросите повторное письмо</p>
				</div>

				<!-- Восстановление пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentNewPassword'">
					<h5 class="modal-title">Восстановление пароля</h5>
					<p class="modal-subtitle">На почту {{ storeField.fields.email.title }} отправлен новый пароль</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn" @click="openModal('authorization')">Войти</button>
					</div>
				</div>

				<!-- Изменения пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentChangeNewPassword'">
					<h5 class="modal-title --center">Изменение пароля</h5>
					<p class="modal-subtitle --center">Ваш пароль изменен!</p>
				</div>

				<!-- Изменения данных -->
				<div class="modal-content" v-if="modalId === 'sentChangeUserProfile'">
					<h5 class="modal-title --center">Добавление адреса</h5>
					<p class="modal-subtitle --center">Ваш адрес добавлен!</p>
				</div>

				<!-- Ответ на feedback -->
				<div class="modal-content" v-if="modalId === 'statusOk'">
					<h5 class="modal-title">Спасибо за обращение!</h5>
					<p class="modal-subtitle">Мы обязательно Вам ответим в течение 3 рабочих дней.</p>
				</div>

				<!-- Промокод: информация о бренд-зоне -->
				<div class="modal-content --modalInfo" v-if="modalId === 'promoCodeBrandZone'">
					<p class="modal-subtitle">
						<strong>Этим летом в Москве</strong> на Голицынском пруду в Парке Горького <strong>Greenfield</strong> открывает атмосферное пространство у воды, где можно сделать паузу посреди городского дня, попробовать чайные напитки и провести время в красивой летней локации.
					</p>
				</div>

				<!-- Промокод: успех -->
				<div class="modal-content modal-content--balance" v-if="modalId === 'promoCodeSuccess'">
					<p class="modal-subtitle --center">
						Вам начислено
						<span class="yellow">
							{{ modalContent?.points ?? 50 }}
							<svg width="20" height="20"><use href="#leaf"></use></svg>
						</span>
					</p>
				</div>

				<!-- Промокод: ошибка -->
				<div class="modal-content" v-if="modalId === 'promoCodeError'">
					<p class="modal-subtitle --center">{{ modalContent?.message }}</p>
				</div>

				<!-- Промокод: нужна регистрация -->
				<div class="modal-content modal-content--reginfo" v-if="modalId === 'promoCodeRegistrationRequired'">
					<p class="modal-subtitle --center">Листочки начисляются на баланс только после регистрации</p>
					<div class="form form-grid">
						<button class="send-button btn btn --small form__btn" type="button" @click="openModal('registration')">Регистрация</button>
					</div>
				</div>

				<!-- Правила игры в Листопад -->
				<div class="modal-content" v-if="modalId === 'rulesGameWords'">
					<h5 class="modal-title">Как играть?!</h5>
					<ul class="modal-content___list">
						<li>Составляйте слова, соединяя буквы.</li>
						<li>Открывайте слова уровня.</li>
						<li>Проходите уровни и зарабатывайте листочки.</li>
						<li>Загружайте чеки, чтобы добавить листочки к общему балансу!</li>
					</ul>
				</div>

				<!-- Акция еще не началась-->
				<div class="modal-content" v-if="modalId === 'promoNotStart'">
					<h5 class="modal-title --center" style="text-align: center">Акция скоро начнётся!</h5>
					<p class="modal-subtitle --center">Уже совсем скоро сможешь загружать чеки и выигрывать призы. Следи за обновлениями!</p>
				</div>

				<!-- Акция закончилась -->
				<div class="modal-content" v-if="modalId === 'promoEnd'">
					<h5 class="modal-title --center" style="text-align: center">Акция завершилась!</h5>
					<p class="modal-subtitle --center">Список победителей ты можешь посмотреть на сайте.</p>
				</div>

				<!-- Сбой облачного провайдера, показывается при заходе на сайт -->
				<div class="modal-notice" v-if="modalId === 'serviceNotice'">
					<h5 class="modal-notice__title">Уважаемые участники Greenfield Club!</h5>
					<div class="modal-notice__text">
						<p>В&nbsp;связи со&nbsp;сбоем в&nbsp;работе облачного провайдера Яндекс сайт клуба временно работает с&nbsp;ограничениями.</p>
						<p>Для входа в&nbsp;личный кабинет Вам необходимо создать новый пароль через кнопку «восстановить пароль».</p>
						<p>Мы продолжаем восстанавливать работу сервисов, включая регистрацию участников и&nbsp;чеков. Обмен баллов на&nbsp;подарки пока недоступен.</p>
					</div>
					<button class="modal-notice__btn btn" type="button" @click="openModal('recoveryPassword')">Восстановить пароль</button>
				</div>

				<!-- Призы на главной modalContent.prize.length -->
				<div class="modal-content --modalInfo" v-if="modalId === 'modalPrize'">
					<div class="modal-prizes" v-if="modalContent">
						<Swiper
							:modules="[Pagination]"
							:slides-per-view="1"
							:space-between="20"
							:centeredSlides="true"
							:observer="true"
							:observeParents="true"
							:observeSlideChildren="true"
							:resizeObserver="true"
							:pagination="{
								el: '.modal-prizes__pagination',
							}"

							class="modal-prizes__swiper spotlight-group"
							ref="galleryRef"
						>
							<SwiperSlide class="modal-prizes__slide " v-for="img in modalContent.images" :key="img">
								<img :data-src="img" class="spotlight" width="375" height="375" :src="img" :alt="modalContent.title" />
							</SwiperSlide>
							<button v-if="modalContent.format === 'physical'" @click="openGallery" class="modal-prizes__zoom-btn">
								<svg width='32' height='32'><use href='#zoom'></use></svg>
							</button>
						</Swiper>
						<SliderPagination v-if="modalContent.images.length > 1" class="modal-prizes__pagination" />
						<div class="modal-prizes__content">
							<h4 class="modal-prizes__title">{{ modalContent.title }}</h4>
							<div class="modal-prizes__price" v-if="modalContent.points">
								<span class="modal-prizes__price-value">{{ modalContent.points }}</span>
								<span class="modal-prizes__price-icon">
									<svg width='32' height='32' class="leaf">
										<use xlink:href="#leaf"></use>
									</svg>
								</span>
							</div>
							<p class="modal-prizes__desc" v-html="modalContent.description"></p>
						</div>
					</div>
				</div>

				<!-- Модалка Обменять -->
				<div class="modal-content --modalPrize" v-if="modalId === 'modalExchange'">
					<h5 class="modal-title">Обменять листочки на приз?</h5>
					<div class="modal-prizes" v-if="modalContent">
						<img class="" width="375" height="375" :src="modalContent.images[0]" alt="изображение" />
						<div class="modal-prizes__content">
							<h4 class="modal-prizes__title">{{ modalContent.title }}</h4>
							<div class="modal-prizes__price">
								<span class="modal-prizes__price-value">{{ modalContent.points }}</span>
								<span class="modal-prizes__price-icon">
									<svg width='32' height='32' class="leaf">
										<use xlink:href="#leaf"></use>
									</svg>
								</span>
							</div>
							<p class="modal-prizes__desc" v-html="modalContent.description"></p>
						</div>
					</div>

					<template v-if="modalContent.format === 'physical' && !storeRequests.userData?.address">
						<button @click="openModal('isNotAddress', { size: 'w585' })" class="modal-prizes__btn btn">Обменять</button>
					</template>
					<template v-else>
						<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						<BaseButtonUploadReceipt class="modal-prizes__btn" v-if="storeRequests.responseError">Загрузить чек</BaseButtonUploadReceipt>
						<button v-else @click="storeRequests.buyPrize(modalContent.id)" :class="['modal-prizes__btn btn', { 'not-hover': storeRequests.loaderBtn }]">
							<span v-if="!storeRequests.loaderBtn">Обменять</span>
							<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
						</button>
					</template>
				</div>

				<!-- Нет адреса -->
				<div class="modal-content" v-if="modalId === 'isNotAddress'">
					<h5 class="modal-title">Первый шаг к подарку —&nbsp;заполнение адреса.</h5>
					<p class="modal-subtitle">Сначала введите адрес доставки, затем Вы сможете выбрать и заказать выбранный подарок</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --blue" type="button" @click="openModal('changeUserProfile')">Добавить адрес</button>
					</div>
				</div>

				<!-- Нет адреса 2 -->
				<div class="modal-content" v-if="modalId === 'isNotAddressPhysical'">
					<h5 class="modal-title">Приз готов к отправке.</h5>
					<p class="modal-subtitle">Чтобы мы смогли отправить Ваш приз, пожалуйста, заполните адрес доставки.</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --blue" type="button" @click="openModal('changeUserProfile')">Указать адрес</button>
					</div>
				</div>

				<!-- Модалка Покупка билета -->
				<div class="modal-content --modalPrize" v-if="modalId === 'modalTicketExchange'">
					<h5 class="modal-title" v-html="modalContent.title "></h5>
					<div class="modal-prizes" v-if="modalContent">
						<img class="" width="612" height="263" :src="modalContent.image" alt="изображение" />
						<div class="modal-prizes__content">
							<div class="modal-prizes__price">
								<span class="modal-prizes__price-value">{{ modalContent.points }}</span>
								<span class="modal-prizes__price-icon">
									<svg width='32' height='32' class="leaf">
										<use xlink:href="#leaf"></use>
									</svg>
								</span>
							</div>
							<div class="modal-prizes__desc-inner" >
								<p class="modal-prizes__desc" v-html="modalContent.description"></p>
								<p class="modal-prizes__desc">После покупки билет будет автоматически добавлен к розыгрышу.</p>
								<p class="modal-prizes__desc">Покупка билета не гарантирует выигрыш. Итоги розыгрыша будут опубликованы на сайте.</p>
							</div>
						</div>
					</div>
					<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
					<BaseButtonUploadReceipt class="modal-prizes__btn" v-if="storeRequests.responseError">загрузить чек</BaseButtonUploadReceipt>
					<button v-else @click="storeRequests.buyPrize(modalContent.id, responseModalId='successBuyTicket')" :class="['modal-prizes__btn btn', , { 'not-hover': storeRequests.loaderBtn }]">
						<span v-if="!storeRequests.loaderBtn">Обменять</span>
						<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
					</button>
				</div>

				<!-- Модалка Общая информация -->
				<div class="modal-content --modalInfo" v-if="modalId === 'modalInfo'">
					<div v-if="modalContent.title" class="" style="font-size: 20px; font-weight: 700;" v-html="modalContent.title"></div>
					<div v-if="modalContent.text" class="" v-html="modalContent.text"></div>
					<div v-if="modalContent.leaf" class="" v-html="modalContent.leaf"></div>
				</div>

				<!-- Модалка описание призов юзера -->
				<div class="modal-content --modalPrize" v-if="modalId === 'modalUserPrize'">
					<h5 class="modal-title" v-if="modalContent.prize.format === 'physical'">Информация о получении</h5>
					<h5 class="modal-title" v-else>Как возпользоваться?</h5>
					<div class="modal-btn-copy-inner" v-if="modalContent.prize.format === 'physical'">
						<template v-if="modalContent.track_number">
							<span >Трек-номер:</span>
							<BaseCopyTooltip class="modal-btn-copy" title="Cкопировать" :text=" modalContent.prize.format === 'promo_code' ? 'Промокод скопирован' : 'Трек-номер скопирован'">
								<template #default="{ show }">
									<span @click="copy(modalContent.track_number , show)">
										{{ modalContent.track_number }}
										<svg width="32" height="32"><use href="#copy_modal"></use></svg>
									</span>
								</template>
							</BaseCopyTooltip>
						</template>
						<p v-else>Трек-номер для отслеживания посылки появится здесь после отправки заказа.</p>
					</div>
					<div class="modal-btn-copy-inner" v-else >
						<template v-if="modalContent.additional_data && modalContent.prize.type !== 'grand'">
							<span>{{ modalContent.prize.format === 'promo_code' ? 'Промокод:' : 'PDF-сертификат:' }}</span>
							<BaseCopyTooltip  class="modal-btn-copy --sertificate" title="Cкачать" :text="modalContent.prize.format === 'promo_code' ? 'Промокод скопирован' : 'Сертификат скачан'">
								<template #default="{ show }" v-if="modalContent.prize.format === 'certificate'">
									<span @click="download(modalContent.additional_data?.id, show)">
										ozon
										<svg width="32" height="32"><use href="#download_modal"></use></svg>
									</span>
								</template>
								<template #default="{ show }" v-else>
									<span @click="copy(modalContent.additional_data?.number, show)">
										{{ modalContent.additional_data?.number }}
										<svg width="32" height="32"><use href="#copy_modal"></use></svg>
									</span>
								</template>
							</BaseCopyTooltip>
						</template>
						<template v-else>
							<p v-if="modalContent.prize.type === 'grand'">{{ modalContent.prize.greeting_text }}</p>
							<p v-else>{{ modalContent.prize.format === 'promo_code' ? 'Промокод появится позднее, следите за обновлениями' : 'Сертификат появится позднее, следите за обновлениями' }}</p>
						</template>

					</div>
					<div v-if="modalContent.prize.type !== 'grand'" class="modal-discription" v-html="modalContent.prize.greeting_text"></div>
				</div>

				<!-- Модалка описание призов юзера за 3 в ряд -->
				<div class="modal-content --modalPrize" v-if="modalId === 'modalUserPrizeMatches'">
					<h5 class="modal-title">Как получить подарок?</h5>
					<p class="modal-subtitle">Подарок почти у Вас! Чтобы узнать, что именно Вам досталось, необходимо загрузить чек и дождаться его одобрения.</p>
					<div class="modal-content__list-inner">
						<h5 class="modal-content__list-title"><strong>Что нужно сделать:</strong></h5>
						<ol>
							<li>Загрузите чек с любым чаем Greenfield.</li>
							<li>Дождитесь положительной проверки — после этого подарок откроется автоматически.</li>
						</ol>
					</div>

					<div class="modal-content__inner-timer">
						<p>🔥 Поторопитесь, подарок сгорит через:</p>
						<div class="modal-content__timer">
							<div class="modal-content__timer-item">
								<span>{{ timeAr[0] }}</span>
								<span>{{ declension(timeAr[0], ['час', 'часа', 'часов']) }}</span>
							</div>
							<div class="modal-content__timer-item">
								<span>{{ timeAr[1] }}</span>
								<span>{{ declension(timeAr[1], ['минута', 'минуты', 'минут']) }}</span>
							</div>
							<div class="modal-content__timer-item">
								<span>{{ timeAr[2] }}</span>
								<span>{{ declension(timeAr[2], ['секунда', 'секунды', 'секунд']) }}</span>
							</div>
						</div>
					</div>

					<BaseButtonUploadReceipt class="modal-info__btn">Загрузить чек</BaseButtonUploadReceipt>
				</div>

				<!-- Приз выбран-->
				<div class="modal-content" v-if="modalId === 'successSetPrize'">
					<h5 class="modal-title --center" style="text-align: center">Приз выбран!</h5>
				</div>

				<!-- Билет куплен-->
				<div class="modal-content" v-if="modalId === 'successBuyTicket'">
					<h5 class="modal-title --center" style="text-align: center">Билет куплен!</h5>
					<div class="modal-prizes">
						<p class="modal-prizes__desc" style="margin-top: -25px" v-html="modalContent.response"></p>
					</div>
				</div>

				<!-- Призы user -->
				<div class="modal-content" v-if="modalId === 'userPrizes'">
					<div class="modal-prizes" v-html="modalContent.greetingText"></div>
				</div>

				<!-- Информация о получении -->
				<div class="modal-content" v-if="modalId === 'downloadReceiptsEnd'">
					<h5 class="modal-title --center">{{ userReceiptsStatus(storeRequests.userData.receipt_status) }}</h5>
				</div>
			</div>
		</Transition>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from {
	opacity: 0;
	transform: translateY(-10%);
}
.v-leave-to {
	opacity: 0;
	transform: translateY(-10%);
}

.modal {
	--transition-time: 0.3s;
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	background-color: rgba(0, 0, 0, 0.5);
	backdrop-filter: blur(10.800000190734863px);
	cursor: pointer;
	overflow-y: auto;
	overflow-x: hidden;
	text-align: center;
	opacity: 0;
	visibility: hidden;
	transition:
		opacity var(--transition-time),
		visibility var(--transition-time);
}

.modal::before {
	content: "";
	display: inline-block;
	vertical-align: middle;
	height: 100%;
}

.modal__container {
	position: relative;
	width: 665px;
	margin: 50px auto;
	display: none;
	vertical-align: middle;
	background: var(--fill-accent-1-1);
	border-radius: 4rem;
	cursor: default;
	text-align: left;
	padding: 0;
	@extend .base-bg;
	@media (max-width: vars.$m) {
		width: 95%;
	}
	@media (max-width: vars.$x480) {
		border-radius: 2.2rem;
	}

	&.w1212 {
		width: 1212px;
		@media (max-width: 1260px) {
			width: 95%;
		}
	}
	&.w740 {
		width: 740px;
		@media (max-width: vars.$m) {
			width: 95%;
		}
	}
	&.w726 {
		width: 726px;
		@media (max-width: vars.$m) {
			width: 95%;
		}
	}
	&.w704 {
		width: 704px;
		@media (max-width: vars.$m) {
			width: 95%;
		}
	}
	&.w585 {
		width: 585px;
		@media (max-width: vars.$m) {
			width: 95%;
		}
	}
	&.w408 {
		width: 408px;
		@media (max-width: vars.$x425) {
			width: 95%;
		}
	}
	&.w400 {
		width: 400px;
		border-radius: 24px;
		@media (max-width: vars.$x425) {
			width: 95%;
		}
		.modal-close {
			position: absolute;
			width: 20px;
			height: 20px;
			top: 15px;
			right: 15px;
			@media (max-width: vars.$x480) {
				top: 10px;
				right: 10px;
				width: 30px;
				height: 30px;
			}
			svg {
				width: 100%;
				height: 100%;
				stroke-width: 2px;
				stroke: #fff;
				transition: transform var(--transition) ease-in;
			}
			@media (hover: hover) {
				&:hover {
					svg {
						transform: scale(1.1);
					}
				}
			}
		}
	}
	&.w320 {
		width: 320px;
		@media (max-width: vars.$x393) {
			width: 95%;
		}
	}
	&.w830 {
		width: 83rem;
		max-width: calc(100vw - 3.2rem);
		box-shadow: inset 0 13px 35px 0 rgba(255, 255, 255, 0.14), 0 16px 33px 0 rgba(0, 0, 0, 0.25);
		@media (max-width: vars.$x480) {
			// 331px на 393px, как в макете
			width: calc(100vw - 6.2rem);
			border-radius: 2.4rem;
		}
		// крестик в спрайте занимает половину своей области: 4rem даёт 2rem линий, как в макете; 1px — рамка контейнера
		.modal-close {
			width: 4rem;
			height: 4rem;
			top: calc(4.4rem - 1px);
			right: calc(4.4rem - 1px);
			@media (max-width: vars.$x480) {
				width: 3rem;
				height: 3rem;
				top: 0.25rem;
				right: 0.45rem;
			}
		}
	}
}

// в макете у текстов обрезка до высоты прописных (leading trim: cap height),
// поэтому вертикальные отступы пересчитаны от строк браузера; 1px — рамка контейнера
.modal-notice {
	display: flex;
	flex-direction: column;
	padding: calc(6.72rem - 1px) calc(6.4rem - 1px) calc(6.4rem - 1px);
	@media (max-width: vars.$x480) {
		padding: calc(2.25rem - 1px) calc(1.8rem - 1px) calc(1.8rem - 1px);
	}
}

.modal-notice__title {
	margin-bottom: 2.9rem;
	padding-right: 4rem;
	font-family: var(--second-family);
	font-weight: 700;
	font-size: 2.8rem;
	line-height: 110%;
	text-transform: uppercase;
	color: var(--text-inverse-100);
	@media (max-width: vars.$x480) {
		margin-bottom: 1.6rem;
		padding-right: 2rem;
		font-size: 2rem;
	}
}

.modal-notice__text {
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
	margin-bottom: 3.5rem;
	font-family: var(--font-family);
	font-weight: 500;
	font-size: 2rem;
	line-height: 120%;
	color: var(--text-inverse-100);
	@media (max-width: vars.$x480) {
		gap: 1.68rem;
		margin-bottom: 2.1rem;
		font-size: 1.4rem;
	}
}

.modal-notice__btn {
	width: 100%;
}

.modal.is-open {
	opacity: 1;
	visibility: visible;
	transition:
		opacity var(--transition-time),
		visibility var(--transition-time);
	.modal__container {
		display: inline-block;
	}
}

.modal__container.modal-open {
	display: inline-block;
}

.fade {
	opacity: 0;
	transition: opacity var(--transition-time);
}

.fade.animate-open {
	opacity: 1;
	transition: opacity var(--transition-time);
}

.fadeInUp {
	opacity: 0;
	transform: translateY(-100px);
	transition:
		opacity var(--transition-time),
		transform var(--transition-time);
}

.fadeInUp.animate-open {
	opacity: 1;
	transform: translateY(0);
	transition:
		opacity var(--transition-time),
		transform var(--transition-time);
}

.disable-scroll {
	position: relative;
	overflow: hidden;
	height: 100vh;
	position: fixed;
	left: 0;
	top: 0;
	width: 100%;
}

.modal-close {
	position: absolute;
	width: 40px;
	height: 40px;
	top: 30px;
	right: 23px;
	@media (max-width: vars.$x480) {
		top: 10px;
		right: 10px;
		width: 30px;
		height: 30px;
	}
	svg {
		width: 100%;
		height: 100%;
		stroke-width: 2px;
		stroke: #fff;
		transition: transform var(--transition) ease-in;
	}
	@media (hover: hover) {
		&:hover {
			svg {
				transform: scale(1.1);
			}
		}
	}
}

.modal__container {
	&:has(.modal-content--reginfo) {
		max-width: 40rem;
	}
}

.modal-content {
	display: grid;
	gap: 30px;
	padding: 62px;

	&--reginfo {
		padding: 3.2rem;
		max-width: 40rem;
		gap: 2.4rem;

		.modal-subtitle {
			font-weight: 500;
			font-size: 1.6rem;
			line-height: 1.2;
			padding-right: 3rem;
			text-align: left !important;
		}
	}

	&--balance {
		padding: 3.2rem 1.6rem;

		.modal-subtitle {
			font-size: 1.6rem;

			span {
				color: var(--text-accent-2-100);
			}

			svg {
				width: 2rem;
				height: 2rem;
				position: relative;
				top: .4rem;
			}
		}
	}

	@media (max-width: vars.$x480) {
		padding: 35px;
		gap: 25px;
	}
	&.--modalInfo {
		padding: 32px;
		gap: 24px;
		:deep(p) {
			font-family: var(--font-family);
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
		}



	}
	&.--modalPrize {
		padding: 70px 62px;
		gap: 35px;
		@media (max-width: vars.$xx) {
			padding: 40px 20px;
			gap: 20px;
		}
		.modal-title {
			font-family: var(--second-family);
			font-weight: 700;
			font-size: 2.8rem;
			line-height: 110%;
			text-transform: uppercase;
			@media (max-width: vars.$xx) {
				font-size: 2.2rem;
			}
		}
		.modal-subtitle {
			font-weight: 400;
			font-size: 2.4rem;
			line-height: 140%;
			@media (max-width: vars.$xx) {
				font-size: 1.8rem;
			}
		}
		.form__text-error {
			font-weight: 700;
			font-size: 2.6rem;
			line-height: 120%;
			text-align: start;
			color: var(--text-accent-2-100);
			@media (max-width: vars.$xx) {
				font-size: 1.6rem;
			}
		}
		.modal-prizes {
			&__title {
				font-size: 2.8rem;
				line-height: 160%;
			}
		}
	}
}
.modal-content___list {
	display: grid;
	gap: 5px;
	li {
		display: flex;
		align-items: center;
		gap: 15px;
		&::before {
			content: '';
			border-radius: 50%;
			background-color: #fff;
			width: 4px;
			height: 4px;
			flex-shrink: 0;
		}
	}
}
.modal-title {
	font-family: var(--second-family);
	font-weight: 500;
	font-size: 28px;
	line-height: 140%;
	text-transform: uppercase;
	color: var(--text-inverse-100);
	@media (max-width: vars.$xx) {
		font-size: 22px;
	}
	@media (max-width: vars.$x393) {
		font-size: 18px;
	}
	&.--center {
		text-align: center;
	}
	&.--fs {
		font-size: 28px;
	}
}
.modal-subtitle {
	font-weight: 500;
	font-size: 20px;
	line-height: 160%;
	@media (max-width: vars.$xx) {
		font-size: 16px;
	}
	a {
		color: inherit;
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}
	&.--center {
		text-align: center;
	}
}
.modal-discription {
	:deep(ol) {
		padding-left: 22px;
	}
	:deep(a) {
		color: inherit;
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}
}
.modal-btn-copy-inner {
	display: flex;
	align-items: center;
	gap: 10px;
	font-weight: 500;
	font-size: 2rem;
	line-height: 120%;
	color: var(--text-inverse-100);
	@media (max-width: vars.$x480) {
		flex-direction: column;
		align-items: start;
		font-size: 1.6rem;
	}
}

.modal-btn-copy {
	font-family: var(--third-family);
	font-weight: 500;
	font-size: 2.2rem;
	line-height: 130%;
	text-transform: uppercase;
	text-align: center;
	color: var(--text-inverse-100);

	border: 1px solid var(--border-accent-2-1);
	border-radius: 1.9rem;
	padding: 0rem 3.2rem;
	width: fit-content;
	height: 5.6rem;
	transition:  var(--transition) ease-in;

	@media (max-width: vars.$x480) {
		padding: 0 2rem;
		font-size: 1.6rem;
		height: 4rem;
		border-radius: 1.2rem;
	}

	&.--sertificate {
		@media (hover: hover) {
		&:hover {
			svg {
				fill:  var(--brand-accent-1-100);
				stroke: transparent;
			}
		}
	}
	}
	@media (hover: hover) {
		&:hover {
			background-color: #fff;
			color: var(--brand-accent-1-100);
			border-color: var(--brand-accent-1-100);
			svg {
				fill:  var(--brand-accent-1-100);
				stroke: var(--text-accent-2-100);
			}
		}
	}
	span {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	svg {
		width: 3.2rem;
		height: 3.2rem;
		fill: var(--text-accent-2-100);
		stroke: var(--brand-accent-1-100);
		transition:  var(--transition) ease-in;
		@media (max-width: vars.$x480) {
			width: 2.4rem;
			height: 2.4rem;
		}
	}
}
.buttons {
	display: grid;
	gap: 56px;
}

.modal-content__desc {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	color: var(--fill-default-1);
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
	}
	ul {
		padding-left: 3rem;
		list-style-type: disc;
		@media (max-width: vars.$x480) {
			padding-left: 1.5rem;
		}
		li {
		}
	}
}
.modal-content__desc-title {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	margin-bottom: 4rem;
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
		margin-bottom: 1rem;
	}
}

.modal-prizes {
	display: grid;
	gap: 15px;
	img {
		flex-shrink: 0;
		pointer-events: none;
		justify-self: center;
	}
	.btn {
		font-size: 1.6rem;
		padding: 1.8rem 4rem;
		@media (max-width: vars.$x480) {
			padding: 1.4rem 4rem;
		}
	}


	&__pagination {
	}

	&__swiper {
		width: 90%;
	}
	&__slide {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	&__zoom-btn {
		position: absolute;
		width: 3.2rem;
		height: 3.2rem;
		z-index: 1;
		bottom: 0;
    	right: 0;
		svg {
			width: 100%;
			height: 100%;
			fill: var(--fill-accent-2-1);
			stroke: transparent;
		}
	}


	&__content {
	}

	&__title {
		font-family: var(--second-family);
		font-weight: 700;
		font-size: 4.8rem;
		line-height: 110%;
		text-transform: uppercase;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		@media (max-width: vars.$x480) {
			font-size: 2.8rem;
		}
	}

	&__price {
		display: flex;
		align-items: center;
		gap: 0.8rem;
	}

	&__price-value {
		font-family: var(--second-family);
		font-weight: 700;
		font-size: 4.8rem;
		line-height: 140%;
		color: var(--text-accent-2-100);

		@media (max-width: vars.$x480) {
			font-size: 2.8rem;
		}
	}

	&__price-icon {
		width: 3.2rem;
		height: 3.2rem;
		display: flex;
		align-items: center;
		justify-content: center;

		@media (max-width: vars.$x480) {
			width: 2.6rem;
			height: 2.6rem;
		}
	}
	&__desc-inner {
		.modal-prizes__desc {
			&:last-child {
				opacity: .4;
			}
		}
	}

	&__desc {
		font-weight: 500;
		font-size: 1.6rem;
		line-height: 140%;
		white-space: pre-line;
		&:not(:last-child) {
			margin-bottom: 10px;
		}

	}


}
.modal-prizes__btn {
	// position: relative;
	// display: flex;
	// align-items: center;
	// gap: 20px;
	// border: 1px solid var(--border-inverse-3);
	// border-radius: var(--border-radius);
	// padding: 20px 10px;
	// color: var(--text-accent-1-100);
	// @media (max-width: vars.$x480) {
	// 	flex-direction: column;
	// 	text-align: start;
	// }
	// @media (hover: hover) {
	// 	&:hover {
	// 		background: var(--fill-accent-1-3);
	// 	}
	// }
	// &.active {
	// 	color: var(--text-inverse-100);
	// 	background: var(--fill-accent-1-1);
	// 	border-color: var(--fill-accent-1-1);
	// 	svg {
	// 		display: block;
	// 	}
	// 	&.pointer-events-none {
	// 		pointer-events: auto;
	// 	}
	// }
	// &.pointer-events-none {
	// 	pointer-events: none;
	// }
	// svg {
	// 	display: none;
	// 	position: absolute;
	// 	top: 24px;
	// 	right: 24px;
	// 	width: 2.4rem;
	// 	height: 2.4rem;
	// 	stroke: transparent;
	// }
}
.modal-prizes__content {
}
// .modal-prizes__title {
// 	font-weight: 500;
// 	font-size: 2rem;
// 	line-height: 120%;
// 	margin-bottom: 20px;
// 	text-align: start;
// }
// .modal-prizes__subtitle {
// 	font-weight: 400;
// 	font-size: 1.4rem;
// 	line-height: 140%;
// }
.modal-content {

	&__list-inner {
		font-weight: 400;
		font-size: 2.4rem;
		line-height: 140%;
		@media (max-width: vars.$xx) {
			font-size: 1.8rem;
		}
		ol {
			margin: 0;
			li {
				&:not(:last-child) {
					margin-bottom: 2px;
				}
			}
		}

	}

	&__list-title {
		font-weight: 400;
		font-size: 2.4rem;
		line-height: 140%;
		margin-bottom: 10px;
		@media (max-width: vars.$xx) {
			font-size: 1.8rem;
		}
	}

	&__inner-timer {
		display: flex;
		align-items: center;
		flex-direction: column;
		gap: 5px;
		@extend .base-bg-light;
		border: 1px solid var(--border-accent-2-4);
		border-radius: 1.6rem;
		padding: 16px 8px 16px;
		p {
			font-weight: 400;
			font-size: 2.4rem;
			line-height: 140%;
			text-align: center;
			@media (max-width: vars.$xx) {
				font-size: 1.4rem;
			}
		}
	}

	&__timer {
		width: 100%;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
	}

	&__timer-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		span {
			font-family: var(--font-family);
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
			text-transform: lowercase;
			color: var(--text-inverse-100);
			@media (max-width: vars.$xx) {
				font-size: 1.2rem;
			}
			&:first-child {
				font-family: var(--third-family);
				font-weight: 500;
				font-size: 3.2rem;
				line-height: 140%;
				text-transform: uppercase;
				text-align: center;
				color: var(--text-accent-2-100);
				@media (max-width: vars.$xx) {
					font-size: 1.8rem;
				}
			}
		}
	}
}
.modal-content {

	&--modalInfo {
		.modal-title {
		}
		.modal-subtitle {
		}
	}
}

</style>
